import React, { useId, useMemo, useState } from "react";
import { resolveLocalizedText } from "../../../utils/questionUtils";
import { strings } from "../../../resources/strings";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  QuestionType,
  WorkflowName,
} from "../../../apis/API";
import { WorkflowItem, WorkflowSpec } from "./types";
import {
  buildWorkflowAnswers,
  parseWorkflowPrepopulation,
} from "./workflowAnswers";
import { getFieldError, getFieldInputHints } from "./fieldValidation";
import { getAutocompleteToken } from "./autocompleteTokens";
import {
  resolveConfirmOnFileLabel,
  withRequiredSuffix,
} from "./workflowLabels";
import FloatingLabelField from "./FloatingLabelField";
import AnswerButtonRow from "./AnswerButtonRow";
import ChangeAnswerButton from "./ChangeAnswerButton";
import { AnswerIcon, CHECK_PATH, saveButtonClass } from "./answerButtons";
import BoolContextCard from "../BoolContextCard";
import {
  countryName,
  formatAppointment,
  formatLastConfirmed,
  onFileString,
  parseOnFile,
} from "./confirmOnFileDisplay";
import type { WorkflowAnswer } from "./WorkflowQuestion";

const wf = strings.reportForm.workflow;

export interface ConfirmOnFileFormProps {
  question: LocalizedParticipantQuestion;
  /** Resolved CONFIRM_ON_FILE workflow (address/phone/appointment/email/emergencyContact). */
  workflowName: WorkflowName;
  /** Resolved CONFIRM_ON_FILE spec. */
  spec: WorkflowSpec;
  /** Raw `questionPrepopulation.values` JSON for the current workflow (on-file data). */
  prepopulationValues?: string;
  /** Prior answer, for back-nav re-display (reflects the root selection). */
  defaultAnswer?: IdentifiableAnswerV3Input;
  onAnswer: (answer: WorkflowAnswer) => void;
}

/** Fields shown on the "on file" card / serialized but NOT editable inputs. */
const CARRIED_FIELDS: Partial<Record<WorkflowName, string[]>> = {
  // country rides along in the answer (echoed from on-file) but has no input.
  addressConfirmation: ["country"],
};

/**
 * CONFIRM_ON_FILE workflow UI (Android `ConfirmOnFileForm`). Shows the on-file
 * data in a read-only card, then a confirm root:
 *
 * - **addressConfirmation** (`branchOpensOn: CHANGED`, non-adaptive): "Correct"
 *   → `answer:false` + `{}`. "Changed" → edit step → `answer:true` + the flat
 *   field object (`country` carried from on-file). No `changed` key.
 * - **appointment** (confirm-only, no fields): Yes → `answer:true`, No →
 *   `answer:false`; both emit `{}`. No edit step.
 * - **email / phoneConfirmation / emergencyContact** (adaptive
 *   confirm-**or-collect**, via `spec.adaptive`; IHA-395/EHA-908, EHA-861/
 *   IHA-407): the flow depends on whether any qualifying field is on file —
 *   - **confirm flow** (qualifying data present): Correct/Changed root.
 *     "Correct" → `answer:false` + `{...on-file fields, changed:false}` (echoed,
 *     blanks omitted). "Changed" → edit → `answer:true` +
 *     `{...entered fields, changed:true}`.
 *   - **ask flow** (no qualifying data): a literal Yes/No root ("Do you have…?").
 *     "Yes" → edit → `answer:true` + `{...entered fields}` (**no** `changed`).
 *     "No" → `answer:false` + `{}` (no `changed`).
 *   Matches the agency webapp's `ConfirmOrCollectWorkflowAnswer`, which reads
 *   the **presence** (not truthiness) of `changed` to tell the flows apart. The
 *   confirm/ask headline source differs per workflow (server prompt vs catalog)
 *   — see `rootHeadline` and `AdaptiveConfirmConfig`.
 *
 * The `answer` bool tracks `branchOpensOn` (NOT a literal Yes/No) for the
 * confirm flow, matching the Android `V3AnswerAssembler`; the ask flow is a
 * literal Yes/No like `StructuredForm`.
 */
const ConfirmOnFileForm: React.FC<ConfirmOnFileFormProps> = ({
  question,
  workflowName,
  spec,
  prepopulationValues,
  defaultAnswer,
  onAnswer,
}) => {
  const questionId = question.id ?? "";
  const wd = question.workflowDefinition;
  const confirmOnly = spec.fields.length === 0; // appointment
  const isAddress = workflowName === WorkflowName.addressConfirmation;
  const isPhone = workflowName === WorkflowName.phoneConfirmation;
  const isEmail = workflowName === WorkflowName.email;
  const isEmergency = workflowName === WorkflowName.emergencyContact;

  // Raw on-file object (all keys, incl. display-only metadata) + the spec-field
  // allowlist seed (for carrying values into the edit/echo payload). Memoized so
  // typing in the edit step doesn't re-parse the JSON each keystroke.
  const onFile = useMemo(
    () => parseOnFile(prepopulationValues),
    [prepopulationValues],
  );
  const seedItem = useMemo(
    () => parseWorkflowPrepopulation(prepopulationValues, spec),
    [prepopulationValues, spec],
  );

  // Adaptive confirm-or-collect (email/phone/emergencyContact): the "confirm"
  // flow (Correct/Changed + `changed` key) when any qualifying field is on file,
  // else the "collect"/ask flow (literal Yes/No, no `changed`). Non-adaptive
  // CONFIRM_ON_FILE workflows (address/appointment) have no `spec.adaptive` and
  // are unaffected.
  const adaptive = spec.adaptive;
  const qualifiesOnFile =
    !!adaptive &&
    adaptive.qualifyingFields.some((k) => onFileString(onFile[k]).length > 0);
  // Ask flow (adaptive, no qualifying data). The confirm flow is the remaining
  // adaptive case — `adaptive && !collectMode` — so no separate flag is kept.
  const collectMode = !!adaptive && !qualifiesOnFile;
  // Confirm-branch root labels/header live under this stepId (email uses
  // `rootEmailPresent`; phone/emergency use `root`); non-adaptive → `root`.
  const confirmStepId = adaptive?.confirmStepId ?? "root";

  const carried = CARRIED_FIELDS[workflowName] ?? [];
  const editableKeys = spec.fields
    .map((f) => f.fieldKey)
    .filter((k) => !carried.includes(k));
  // Address edit seeds from on-file (correcting an existing address); phone and
  // email edit start blank (re-enter/collect fresh) — matches Android.
  const [inputs, setInputs] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const k of editableKeys) {
      init[k] = isAddress ? (seedItem[k] ?? "") : "";
    }
    return init;
  });

  // Build a `{ fieldKey: value }` object over the editable fields, reading each
  // value via `read` and DROPPING blanks — the adaptive confirm/ask wire shape
  // omits empty fields. Shared by the "Correct" echo (reads on-file) and Save
  // (reads inputs) so the omit-blank invariant lives in one place.
  const collectNonBlank = (
    read: (key: string) => string,
  ): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const k of editableKeys) {
      const v = read(k);
      if (v) {
        out[k] = v;
      }
    }
    return out;
  };

  // Always open on the root confirm screen. The edit step offers a "Change my
  // answer" control back to it (EHA-903); the prior selection drives aria-pressed.
  const [step, setStep] = useState<"root" | "edit">("root");
  const rootAnswer = defaultAnswer?.answer ?? null;

  // NOT lang-marked (SC 3.1.2), deliberately: unlike the other workflow forms,
  // this one's visible headline usually comes from the workflow label catalog
  // via resolveConfirmOnFileLabel, not from question.localization, and that
  // resolver does not report which locale it matched. Marking it from the
  // question's locale would be wrong whenever the catalog supplied the text.
  // Logged in A11Y_ISSUES.md.
  const headline = resolveLocalizedText(question.localization) ?? "";
  // Confirm/ask headline asymmetry (EHA-861/IHA-407): the server prompt
  // (`question.localization`) is the ASK question for email ("Do you have an
  // email address?") but the CONFIRM question for phone/emergency ("Has your
  // phone number changed…?"). So each adaptive workflow names a catalog key for
  // the OTHER branch and falls back to the server prompt when the key is absent.
  const headlineKey = collectMode
    ? adaptive?.askHeadlineKey
    : adaptive // confirm flow (adaptive + not collect); non-adaptive → undefined
      ? adaptive.confirmHeadlineKey
      : undefined;
  const rootHeadline = headlineKey
    ? /* istanbul ignore next -- the catalog always carries the adaptive
         headline; the `?? headline` guards a missing key (a programming error). */
      (resolveConfirmOnFileLabel(
        workflowName,
        wd,
        confirmStepId,
        headlineKey,
      ) ?? headline)
    : headline;
  const titleId = useId();

  const emit = (
    answer: boolean,
    items: WorkflowItem[],
    extra?: Record<string, unknown>,
  ) =>
    onAnswer({
      questionId,
      questionType: QuestionType.workflow,
      workflowName,
      answer,
      workflowAnswers: JSON.stringify({
        ...buildWorkflowAnswers(workflowName, spec, items),
        ...extra,
      }),
    });

  // --- Root handlers -------------------------------------------------------
  // Email's collect branch (no on-file value) is a literal Yes/No, like
  // `confirmOnly` — its "correct"/"changed" wording only applies once an
  // on-file value exists.
  const affirmativeLabel =
    confirmOnly || collectMode
      ? strings.questionAnswers.yes
      : (resolveConfirmOnFileLabel(
          workflowName,
          wd,
          confirmStepId,
          "correctBtn",
        ) ?? strings.questionAnswers.yes);
  const negativeLabel =
    confirmOnly || collectMode
      ? strings.questionAnswers.no
      : (resolveConfirmOnFileLabel(
          workflowName,
          wd,
          confirmStepId,
          "changedBtn",
        ) ?? strings.questionAnswers.no);

  const handleAffirmative = () => {
    if (confirmOnly) {
      emit(true, []); // appointment Yes
    } else if (collectMode) {
      setStep("edit"); // ask flow "Yes" → collect a fresh value
    } else if (adaptive) {
      // Adaptive "Correct" (confirm flow): echo the on-file non-blank fields +
      // changed:false. Built via `extra` with empty items so blank fields are
      // omitted (the shared item builder would coerce missing fields to "").
      emit(false, [], {
        ...collectNonBlank((k) => onFileString(onFile[k])),
        changed: false,
      });
    } else {
      emit(false, []); // address "Correct" → {}
    }
  };

  const handleNegative = () => {
    if (confirmOnly) {
      emit(false, []); // appointment No
    } else if (collectMode) {
      emit(false, []); // ask flow "No" → {} (no `changed` key)
    } else {
      setStep("edit"); // confirm flow "Changed" (adaptive + address) → edit
    }
  };

  // appointment Yes/No and the ask flow map answer true/false like a literal
  // Yes/No; the confirm flow (address + adaptive) always maps Correct→false /
  // Changed→true, regardless of the spec's `branchOpensOn` (email's confirm
  // flow keeps this mapping even though its `branchOpensOn` is "YES").
  const affirmativePressed =
    confirmOnly || collectMode ? rootAnswer === true : rootAnswer === false;
  const negativePressed =
    confirmOnly || collectMode ? rootAnswer === false : rootAnswer === true;

  // --- Edit handlers -------------------------------------------------------
  // Required keys come from the spec (registry is the source of truth): phone
  // marks only `mobile` required; emergency requires both name + phone.
  const requiredKeys = editableKeys.filter(
    (k) => spec.fields.find((f) => f.fieldKey === k)?.required,
  );
  // Save needs every required field filled AND every field free of format
  // errors (e.g. a malformed `email` — EHA-904's input-format validation).
  const canSave =
    requiredKeys.every((k) => inputs[k].trim().length > 0) &&
    editableKeys.every((k) => getFieldError(k, inputs[k]) === undefined);

  const handleSave = () => {
    if (adaptive) {
      // Adaptive edit: emit only the non-blank entered fields (blanks omitted).
      // Confirm flow "Changed" carries `changed:true`; ask flow "Yes" carries
      // no `changed` key.
      const fields = collectNonBlank((k) => inputs[k].trim());
      emit(true, [], collectMode ? fields : { ...fields, changed: true });
      return;
    }
    // Address "Changed": carried `country` + all fields (existing behavior).
    const item: WorkflowItem = {};
    for (const k of carried) {
      item[k] = seedItem[k] ?? "";
    }
    for (const k of editableKeys) {
      item[k] = inputs[k].trim();
    }
    emit(true, [item]);
  };

  // --- On-file card --------------------------------------------------------
  const renderCard = () => {
    if (confirmOnly) {
      const appt = formatAppointment(onFile.time, onFile.agencyTimezone);
      if (!appt) {
        return null;
      }
      const dateLabel =
        resolveConfirmOnFileLabel(workflowName, wd, "root", "dateLabel") ??
        "Date";
      const timeLabel =
        resolveConfirmOnFileLabel(workflowName, wd, "root", "timeLabel") ??
        "Time";
      return (
        <BoolContextCard
          context={{ [dateLabel]: appt.date, [timeLabel]: appt.time }}
        />
      );
    }

    // Ask flow (no qualifying data on file) shows no on-file card.
    if (collectMode) {
      return null;
    }

    if (isPhone) {
      // Confirm flow only (ask flow returned null above), so mobile OR other is
      // present — no need to re-check for "nothing on file" here.
      const mobile = onFileString(onFile.mobile);
      const other = onFileString(onFile.other);
      /* istanbul ignore next -- phoneConfirmation always has these catalog labels;
         the defaults guard a missing key (a programming error) for type-safety. */
      const mainLabel =
        resolveConfirmOnFileLabel(workflowName, wd, "root", "mobile") ?? "Main";
      /* istanbul ignore next -- see above (catalog always resolves "other"). */
      const otherLabel =
        resolveConfirmOnFileLabel(workflowName, wd, "root", "other") ?? "Other";
      return (
        <BoolContextCard
          context={{ [mainLabel]: mobile || "—", [otherLabel]: other || "—" }}
        />
      );
    }

    if (isEmail) {
      // Confirm flow only (ask flow returned null above) → email is present.
      const email = onFileString(onFile.email);
      /* istanbul ignore next -- email always has this catalog label; the
         `?? "Email"` guards a missing key (a programming error) for type-safety. */
      const emailLabel =
        resolveConfirmOnFileLabel(workflowName, wd, "entry", "email") ??
        "Email";
      return <BoolContextCard context={{ [emailLabel]: email }} />;
    }

    if (isEmergency) {
      // Confirm flow only (ask flow returned null above) → `phone` is present;
      // `name` may be blank (it never gates the confirm flow), so it falls back
      // to an em-dash.
      const name = onFileString(onFile.name);
      const phone = onFileString(onFile.phone);
      /* istanbul ignore next -- emergencyContact always has these entry labels;
         the defaults guard a missing key (a programming error) for type-safety. */
      const nameLabel =
        resolveConfirmOnFileLabel(workflowName, wd, "entry", "name") ?? "Name";
      /* istanbul ignore next -- see above (catalog always resolves "phone"). */
      const phoneLabel =
        resolveConfirmOnFileLabel(workflowName, wd, "entry", "phone") ??
        "Phone Number";
      return (
        <BoolContextCard
          context={{ [nameLabel]: name || "—", [phoneLabel]: phone }}
        />
      );
    }

    // Address: custom stacked-value card (no right-hand labels per Figma).
    const street = onFileString(onFile.streetAddress);
    const city = onFileString(onFile.city);
    const state = onFileString(onFile.state);
    const zip = onFileString(onFile.zipCode);
    const country = countryName(onFile.country);
    if (!street && !city && !state && !zip && !country) {
      return null;
    }
    return (
      <div
        data-testid="address-on-file-card"
        className="rounded-lg bg-[#E8EDF2] shadow divide-y divide-gray-200 text-black"
      >
        {street && <div className="px-4 py-3 min-h-[44px]">{street}</div>}
        {city && <div className="px-4 py-3 min-h-[44px]">{city}</div>}
        {(state || zip) && (
          <div className="flex justify-between px-4 py-3 min-h-[44px]">
            <span>{state}</span>
            <span>{zip}</span>
          </div>
        )}
        {country && <div className="px-4 py-3 min-h-[44px]">{country}</div>}
      </div>
    );
  };

  const onFileHeaderKey = confirmOnly
    ? "scheduledAppt"
    : isPhone
      ? "lastReportedPhone"
      : isEmail
        ? "emailAddressOnFile"
        : isEmergency
          ? "emergencyContactOnFile"
          : "addressOnFile";
  const onFileHeader = resolveConfirmOnFileLabel(
    workflowName,
    wd,
    confirmStepId,
    onFileHeaderKey,
  );
  const lastConfirmedDate = isAddress
    ? formatLastConfirmed(onFile.lastConfirmedAt)
    : null;
  const lastConfirmedLabel = resolveConfirmOnFileLabel(
    workflowName,
    wd,
    "root",
    "lastConfirmed",
  );

  // --- Edit step -----------------------------------------------------------
  if (step === "edit") {
    const entryTitle =
      resolveConfirmOnFileLabel(workflowName, wd, "entry", "title") ?? headline;
    return (
      <div
        data-testid="confirm-on-file-form"
        className="flex flex-col gap-6 overflow-y-auto"
      >
        <h2 className="text-2xl text-black">{entryTitle}</h2>

        <div className="flex flex-col gap-4">
          {editableKeys.map((key, i) => {
            const label =
              resolveConfirmOnFileLabel(workflowName, wd, "entry", key) ?? key;
            // Phone "Other" is optional and disabled until "Main" is entered.
            const disabled =
              isPhone && key === "other" && !inputs.mobile.trim();
            // Format hints/error per field key (e.g. email keyboard + "enter a
            // valid email address"); no-op for keys without a registered rule.
            const { type, inputMode } = getFieldInputHints(key);
            return (
              <FloatingLabelField
                key={key}
                label={withRequiredSuffix(label, requiredKeys.includes(key))}
                required={requiredKeys.includes(key)}
                value={inputs[key]}
                disabled={disabled}
                autoFocus={i === 0}
                testId={`confirm-field-${key}`}
                type={type}
                inputMode={inputMode}
                error={getFieldError(key, inputs[key])}
                autoComplete={getAutocompleteToken(workflowName, key)}
                onChange={(v) => setInputs((prev) => ({ ...prev, [key]: v }))}
              />
            );
          })}
        </div>

        <button
          type="button"
          data-testid="confirm-save"
          onClick={handleSave}
          disabled={!canSave}
          className={saveButtonClass}
        >
          <AnswerIcon testId="save-continue-icon" path={CHECK_PATH} />
          {wf.saveContinue}
        </button>

        <ChangeAnswerButton onClick={() => setStep("root")} />
      </div>
    );
  }

  // --- Root step -----------------------------------------------------------
  const card = renderCard();
  return (
    <div data-testid="confirm-on-file-form" className="flex flex-col gap-6">
      <h2 id={titleId} className="text-2xl text-black">
        {rootHeadline}
      </h2>

      {card && (
        <div className="flex flex-col gap-2">
          {onFileHeader && (
            <span className="text-sm text-[#515964]">{onFileHeader}</span>
          )}
          {card}
          {lastConfirmedDate && (
            <span className="text-sm text-[#515964]">
              {lastConfirmedLabel}: {lastConfirmedDate}
            </span>
          )}
        </div>
      )}

      <AnswerButtonRow
        onYes={handleAffirmative}
        onNo={handleNegative}
        yesSelected={affirmativePressed}
        noSelected={negativePressed}
        yesLabel={affirmativeLabel}
        noLabel={negativeLabel}
        yesTestId="confirm-affirmative"
        noTestId="confirm-negative"
      />
    </div>
  );
};

export default ConfirmOnFileForm;
